import clsx from 'clsx';
import { Pagination, PaginationProps } from 'components/UI/V3/Pagination/Pagination';
import styles from 'components/UI/V3/Table/Table.module.scss';
import React from 'react';

type TableProps = React.ComponentProps<'table'> & {
    fullHeight?: boolean;
    firstColumnFixed?: boolean;
    pagination?: PaginationProps;
    tableClassName?: string;
    wrapperClassName?: string;
    onPageChange?: (page: number) => void;
    onSizeChange?: (size: number) => void;
};

export const Table: React.FC<TableProps> = ({
    children,
    className,
    fullHeight,
    pagination,
    onPageChange,
    onSizeChange,
    firstColumnFixed,
    tableClassName,
    wrapperClassName,
    ...props
}) => {
    const ref = React.useRef<HTMLDivElement>(null);

    const [isScrolled, setIsScrolled] = React.useState(false);

    const handleScroll = React.useCallback((event: Event) => {
        setIsScrolled((event.target as HTMLDivElement).scrollLeft > 0);
    }, []);

    React.useEffect(() => {
        ref.current?.addEventListener('scroll', handleScroll);

        return () => {
            ref.current?.removeEventListener('scroll', handleScroll);
        };
    }, [handleScroll]);

    return (
        <div
            className={clsx(styles.root, className, {
                [styles.fullHeight]: fullHeight
            })}
        >
            <div
                ref={ref}
                className={clsx(styles.scrollWrapper, wrapperClassName)}
            >
                <table
                    className={clsx(styles.table, tableClassName, {
                        [styles.scrolled]: isScrolled,
                        [styles.firstColumnFixed]: firstColumnFixed
                    })}
                    {...props}
                >
                    {children}
                </table>
            </div>

            {pagination && (
                <Pagination
                    {...pagination}
                    onPageChange={onPageChange}
                    onSizeChange={onSizeChange}
                    className={styles.pagination}
                />
            )}
        </div>
    );
};
